/**
 * ArchivePage — top-level archive hub (under Control Panel → خدمات إضافية → الأرشيف).
 * Platforms grid + centralized "AI Chat Archives" banner.
 * Uses the unified DashboardLayout (header + breadcrumbs + back-btn).
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, MessageSquare, Sparkles, ChevronRight } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { usePerms } from '../permissions/PermissionsContext';

const PLATFORMS = [
  { id: 'asb606-admin', nameAr: 'ASB606 ADMIN',     nameEn: 'ASB606 ADMIN',     color: '#d4af37', active: true,  route: '/control/archive/asb606-admin' },
  { id: 'cloudgm',      nameAr: 'CLOUDGM',          nameEn: 'CLOUDGM',          color: '#1E9BD9', active: true,  route: '/control/archive/cloudgm' },
  { id: 'cloudgm-biz',  nameAr: 'CLOUDGM Business', nameEn: 'CLOUDGM Business', color: '#8B5CF6', active: true,  route: '/control/archive/cloudgm-business' },
  { id: '606-fitness',  nameAr: '606 FITNESS',      nameEn: '606 FITNESS',      color: '#10B981', active: true,  route: '/control/archive/606-fitness' },
  { id: '606-mediadocs',nameAr: '606 MEDIADOCS',    nameEn: '606 MEDIADOCS',    color: '#F97316', active: false, route: '/control/archive/606-mediadocs' },
  { id: '606-mt5',      nameAr: '606 MT5 TRADING',  nameEn: '606 MT5 TRADING',  color: '#06B6D4', active: false, route: '/control/archive/606-mt5' },
];

export default function ArchivePage() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();

  return (
    <DashboardLayout
      title="الأرشيف"
      titleEn="Archive"
      subtitle="سجلّات مؤرشفة قبل الحذف النهائي — حسب المنصّة"
      subtitleEn="Archived records before permanent deletion — by platform"
      screenCode="ARC-001"
      backPath="/dashboard"
      onBack={() => navigate('/dashboard')}
      breadcrumb={[{ labelAr: 'الأرشيف', labelEn: 'Archive' }]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="archive-page">
        {/* Centralized AI Chat Archives banner */}
        <motion.button
          type="button"
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.995 }}
          onClick={() => navigate('/control/archive/ai-chats')}
          className="w-full text-start bg-gradient-to-r from-purple-600/20 via-fuchsia-500/10 to-transparent border border-purple-500/40 hover:border-purple-500/70 rounded-2xl p-5 mb-6 transition-all group flex items-center gap-4 overflow-hidden"
          data-testid="ai-chat-archive-btn"
        >
          <div className="w-14 h-14 rounded-2xl bg-purple-500/20 border border-purple-500/40 text-purple-300 flex items-center justify-center shrink-0 shadow-lg">
            <MessageSquare className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-bold text-white">
                {language === 'ar' ? 'أرشيف محادثات AI' : 'AI Chat Archives'}
              </h3>
              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-purple-300 uppercase tracking-wider bg-purple-500/15 border border-purple-500/40 px-1.5 py-0.5 rounded">
                <Sparkles className="h-2.5 w-2.5" />
                {language === 'ar' ? 'مركزي' : 'Central'}
              </span>
            </div>
            <p className="text-xs text-white/60 mt-1 line-clamp-2">
              {language === 'ar'
                ? 'كل المحادثات التي تمّت مراجعتها من مركز المراجعة، مؤرشفة هنا مع ملاحظاتها.'
                : 'All chats reviewed via the Moderation Center, archived here with their notes.'}
            </p>
          </div>
          <ChevronRight className={`h-6 w-6 text-purple-300/70 group-hover:text-purple-300 transition-colors shrink-0 ${language === 'ar' ? 'rotate-180' : ''}`} />
        </motion.button>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visiblePages(PLATFORMS, 'route').map((p, i) => (
            <motion.button
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              whileHover={p.active ? { y: -5 } : {}}
              onClick={() => p.active && navigate(p.route)}
              disabled={!p.active}
              className={`group relative bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-6 text-start transition-all ${
                p.active
                  ? 'border-white/10 hover:border-[#d4af37]/40 cursor-pointer'
                  : 'border-white/5 opacity-50 cursor-not-allowed'
              }`}
              data-testid={`archive-platform-${p.id}`}
            >
              <div
                className="w-14 h-14 rounded-xl mb-3 flex items-center justify-center border"
                style={{ backgroundColor: `${p.color}15`, borderColor: `${p.color}40` }}
              >
                <span className="text-2xl font-bold" style={{ color: p.color }}>{p.nameAr.split(' ')[0].charAt(0)}</span>
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? p.nameAr : p.nameEn}
              </h3>
              <p className="text-xs text-[#71717A]">
                {p.active
                  ? (language === 'ar' ? 'انقر لإدارة الأرشيف' : 'Click to manage archive')
                  : (language === 'ar' ? 'قريباً' : 'Coming soon')}
              </p>
              {!p.active && (
                <Lock className="absolute top-3 left-3 h-4 w-4 text-[#71717A]" />
              )}
            </motion.button>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}
